import { useState, useEffect } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import * as AppPics from 'assets/images';
import { AiOutlineEye } from 'react-icons/ai';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import TablePaginate from 'components/Paginate';
import useRefunds from 'hooks/API/GET/useRefunds';
import useRefundStats from 'hooks/API/GET/useRefundStats';
import useDebounce from 'hooks/useDebounce';

function parseQuery(search) {
  const p = new URLSearchParams(search);
  return {
    status: p.get('status') || '',
    search: p.get('search') || '',
    dateFrom: p.get('dateFrom') || '',
    dateTo: p.get('dateTo') || '',
    page: Number(p.get('page')) || 0,
  };
}

/** Serialise filter state back to a query string for bookmarkable URLs. */
function buildQuery(filters) {
  const p = new URLSearchParams();
  if (filters.status) p.set('status', filters.status);
  if (filters.search) p.set('search', filters.search);
  if (filters.dateFrom) p.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) p.set('dateTo', filters.dateTo);
  if (filters.page > 0) p.set('page', filters.page);
  return p.toString();
}

/** Map status value to a Bootstrap badge class. */
const STATUS_BADGE = {
  pending: 'badge bg-warning',
  under_review: 'badge bg-primary',
  approved: 'badge bg-success',
  rejected: 'badge bg-danger',
  processed: 'badge bg-info',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'processed', label: 'Processed' },
];

//  StatsCard

function StatsCard({ label, value, colorClass = 'text-dark' }) {
  return (
    <div className="col-6 col-md-4 col-lg-2 mb-4">
      <div
        className="card h-100 text-center p-4 border-0 shadow-sm"
        style={{ backgroundColor: '#fff', borderRadius: '12px' }}
      >
        <p
          className="mb-2 text-muted fw-medium"
          style={{ fontSize: '0.95rem', letterSpacing: '0.02rem', textTransform: 'uppercase' }}
        >
          {label}
        </p>
        <h2 className={`mb-0 fw-800 ${colorClass}`} style={{ fontSize: '1.8rem' }}>
          {value}
        </h2>
      </div>
    </div>
  );
}

function RefundListPage() {
  const history = useHistory();
  const location = useLocation();

  const [filters, setFilters] = useState(() => parseQuery(location.search));
  const [searchTerm, setSearchTerm] = useState(filters.search);
  const [enableFilter, setEnableFilter] = useState(
    !!(filters.status || filters.dateFrom || filters.dateTo)
  );

  const debouncedSearch = useDebounce(searchTerm, 600);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, search: debouncedSearch, page: 0 }));
  }, [debouncedSearch]);

  const refundsQuery = useRefunds({ enabled: true, params: filters });
  const statsQuery = useRefundStats({ enabled: true });

  const body = refundsQuery.data?.data?.data || {};
  const refunds = body.result || [];
  const meta = body.meta || { total: 0, limit: 40 };

  const stats = statsQuery.data?.data?.data || null;

  useEffect(() => {
    const qs = buildQuery(filters);
    history.replace({ search: qs ? `?${qs}` : '' });
  }, [filters]);

  //  filter handlers

  const applyFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value, page: 0 }));

  const performSearch = (value) => {
    setSearchTerm(value);
    setFilters((prev) => ({ ...prev, search: value, page: 0 }));
  };

  const clearAllFilters = () => {
    setFilters({
      status: '',
      search: '',
      dateFrom: '',
      dateTo: '',
      page: 0,
    });
    setSearchTerm('');
    setEnableFilter(false);
    history.replace(location.pathname);
  };

  const handlePageChange = (newPage) => setFilters((prev) => ({ ...prev, page: newPage }));

  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '—';

  const formatCurrency = (amount, currency = 'INR') =>
    `${currency === 'INR' ? '₹' : currency}${Number(amount || 0).toLocaleString('en-IN')}`;

  const isLoading = refundsQuery.isLoading;

  return (
    <div className="mainWraper coursetabsection">
      <div className="tab">
        <ul className="tab__menu">
          <li className="back">
            <button className="btn-back" onClick={() => history.push('/console')}>
              <img src={AppPics.BackSvg} alt="Back" />
            </button>
          </li>
        </ul>

        <div className="tab__content ___adjust___tab___padding">
          {/* Breadcrumb */}
          <div className="tab__content-actHead">
            <AppBreadcrumbs
              links={[{ label: 'Dashboard', to: '/console' }]}
              current="Refund Requests"
            />
          </div>

          <div className="tab__content-actBody">
            <div className="px-5 py-5">
              <div className="tab__contentMenu">
                <ul className="tab__contentMenu-list">
                  <li>
                    <h2 className="prime-head p-0 boldtitle text-nowrap">Refund Requests</h2>
                  </li>
                </ul>

                <div className="mainHeader__search">
                  <ul className="tab__contentMenu-list">
                    <li className="relative">
                      <input
                        className="mainHeader__searchInput"
                        placeholder="Search User, Course..."
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            performSearch(searchTerm);
                          }
                        }}
                      />
                      <span
                        className="mainHeader__searchBtn"
                        onClick={() => performSearch(searchTerm)}
                      >
                        <img src={AppPics.TopMenuSearchSvg} alt="Search" />
                      </span>
                    </li>
                    <li className="px-4 flex items-center">
                      <button
                        type="button"
                        onClick={() => setEnableFilter(!enableFilter)}
                        className="flex items-center gap-1"
                      >
                        <img src={AppPics.TabFilterSvg} alt="Filter" />
                        <span className="viewsettingtitle">Filter</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              {/*  Stats row  */}
              <div className="row mb-4">
                <StatsCard label="Total" value={stats?.total ?? '—'} />
                <StatsCard
                  label="Pending"
                  value={stats?.pending ?? '—'}
                  colorClass="text-warning"
                />
                <StatsCard
                  label="Processed"
                  value={stats?.processed ?? '—'}
                  colorClass="text-success"
                />
                <StatsCard
                  label="Rejected"
                  value={stats?.rejected ?? '—'}
                  colorClass="text-danger"
                />
                <StatsCard
                  label="Total Refunded"
                  value={
                    stats
                      ? `₹${Number(stats.totalRefundedAmount || 0).toLocaleString('en-IN')}`
                      : '—'
                  }
                  colorClass="text-info"
                />
              </div>

              {/*  Filters bar  */}
              {enableFilter && (
                <div className="row g-2 align-items-end mb-4 bg-light p-3 rounded shadow-sm">
                  {/* Status dropdown */}
                  <div className="col-12 col-md-3">
                    <label className="form-label mb-1 fw-semibold" style={{ fontSize: '0.82rem' }}>
                      Status
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={filters.status}
                      onChange={(e) => applyFilter('status', e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date from */}
                  <div className="col-6 col-md-2">
                    <label className="form-label mb-1 fw-semibold" style={{ fontSize: '0.82rem' }}>
                      From
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={filters.dateFrom}
                      onChange={(e) => applyFilter('dateFrom', e.target.value)}
                    />
                  </div>

                  {/* Date to */}
                  <div className="col-6 col-md-2">
                    <label className="form-label mb-1 fw-semibold" style={{ fontSize: '0.82rem' }}>
                      To
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={filters.dateTo}
                      onChange={(e) => applyFilter('dateTo', e.target.value)}
                    />
                  </div>

                  <div className="col-12 col-md-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-decoration-none"
                      onClick={clearAllFilters}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              )}

              {/*  Table  */}
              <div className="tab__contentBody">
                <div className="tableWrap">
                  <div className="table-responsive">
                    <table className="table tableWrap__table">
                      <thead className="thead-light __appTheadColor">
                        <tr>
                          <th className="text-center">#</th>
                          <th className="text-center">User</th>
                          <th className="text-center">Course</th>
                          <th className="text-center">Amount</th>
                          <th className="text-center">Reason</th>
                          <th className="text-center">Requested On</th>
                          <th className="text-center">Status</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan={8} className="text-center py-5">
                              <span className="spinner-border spinner-border-sm me-2" />
                              Loading…
                            </td>
                          </tr>
                        ) : refunds.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center py-5 text-muted">
                              No refund requests found
                            </td>
                          </tr>
                        ) : (
                          refunds.map((item, idx) => (
                            <tr
                              key={item._id}
                              style={{ cursor: 'pointer' }}
                              onClick={() => history.push(`/manage/refunds/${item._id}`)}
                            >
                              <td className="text-center">
                                {/* page is 0-based; derive serial number from it */}
                                {filters.page * (meta.limit || 40) + idx + 1}
                              </td>
                              <td>
                                <div className="fw-semibold">{item.userId?.name || '—'}</div>
                                <small className="text-muted">{item.userId?.email || ''}</small>
                              </td>
                              <td className="text-center" style={{ maxWidth: '180px' }}>
                                <span className="text-truncate d-block" title={item.courseName}>
                                  {item.courseName}
                                </span>
                              </td>
                              <td className="fw-semibold text-center">
                                {formatCurrency(item.amount, item.currency)}
                              </td>
                              <td className="text-center">
                                <span
                                  className="text-muted"
                                  style={{ textTransform: 'capitalize' }}
                                >
                                  {(item.reason || '').replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="text-center">{formatDate(item.submittedAt)}</td>
                              <td className="text-center">
                                <span
                                  className={STATUS_BADGE[item.status] || 'badge bg-secondary'}
                                  style={{ color: '#fff' }}
                                >
                                  {(item.status || '').replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                <Link
                                  to={`/manage/refunds/${item._id}`}
                                  className="actions-icons mx-2"
                                  title="View Details"
                                >
                                  <AiOutlineEye style={{ color: '#7e1f8c', fontSize: '24px' }} />
                                </Link>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/*  Pagination  */}
                {!isLoading && meta.total > (meta.limit || 40) && (
                  <TablePaginate
                    currentPage={filters.page + 1}
                    maxCount={meta.total}
                    defaultLimit={meta.limit || 40}
                    rows={[10, 20, 40, 60, 100]}
                    setPage={(page) => handlePageChange(page)}
                    handleChange={(limit) =>
                      setFilters((prev) => ({ ...prev, limit: Number(limit), page: 0 }))
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RefundListPage;
